import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  animateTitle,
  animateLocationCards,
  animateMaps,
  cleanupScrollTriggers,
} from "./gsap";
import {
  BUSINESS_NAME,
  BUSINESS_CONTACT_PHONE,
  BUSINESS_CONTACT_EMAIL,
  BUSINESS_HOURS,
  BUSINESS_ADDRESS,
} from "@/core/constants/business";
import { formatTime } from "@/core/utils/formatTime";

// Format business hours for display
const getFormattedHours = () => {
  const hours = BUSINESS_HOURS.monday;

  if (typeof hours === "string") {
    return hours;
  }

  return `${formatTime(hours.open)} – ${formatTime(hours.close)}`;
};

// Get shortened formatted hours (10:00 AM → 10AM)
const getShortFormattedHours = () => {
  const hours = BUSINESS_HOURS.monday;

  if (typeof hours === "string") {
    return hours;
  }

  return `${hours.open.replace(/^(\d+):00/, "$1")}am - ${hours.close.replace(
    /^(\d+):00/,
    "$1"
  )}pm`;
};

const LocationsSection: React.FC = () => {
  // Refs for animation and DOM elements
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const locationRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mapRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Map loading state for better UX
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);

  // Get formatted hours
  const formattedHours = getFormattedHours();
  const shortFormattedHours = getShortFormattedHours();

  // DC location data from business constants
  const dcLocation = {
    id: "dc-connecticut",
    name: `${BUSINESS_NAME} DC`,
    address: {
      street: BUSINESS_ADDRESS.split(",")[0],
      city: "Washington",
      state: "DC",
      zip: "20008",
      country: "USA",
    },
    coordinates: {
      latitude: 38.9332,
      longitude: -77.058606,
    },
    contact: {
      phone: BUSINESS_CONTACT_PHONE,
      email: BUSINESS_CONTACT_EMAIL,
    },
    googleMapsUrl: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      BUSINESS_ADDRESS
    )}`,
    embedUrl: `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3102.292430228946!2d-77.06085524968114!3d38.9331996064456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7c9b4587e0755%3A0x2821b96583c4bd4e!2s3400%20Connecticut%20Ave%20NW%2C%20Washington%2C%20DC%2020008!5e0!3m2!1sen!2sus!4v1650000000000!5m2!1sen!2sus`,
  };

  // Handle iframe load/error events
  const handleMapLoad = () => {
    console.log("✅ Google Map iframe loaded successfully");
    setMapLoaded(true);
  };

  const handleMapError = () => {
    console.error("❌ Failed to load Google Map iframe");
    setMapError(true);
  };

  useEffect(() => {
    // Initialize refs arrays
    locationRefs.current = new Array(1).fill(null);
    mapRefs.current = new Array(1).fill(null);

    // Apply animations with a small delay to ensure DOM elements are ready
    const animationTimeout = setTimeout(() => {
      if (sectionRef.current && titleRef.current) {
        // Animate the section elements
        animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );

        // Animate the location cards
        const cardRefs = locationRefs.current
          .filter(Boolean)
          .map((ref) => ({ current: ref }));
        if (cardRefs.length > 0) {
          animateLocationCards(cardRefs, { current: sectionRef.current });
        }

        // Animate the maps
        const mapContainerRefs = mapRefs.current
          .filter(Boolean)
          .map((ref) => ({ current: ref }));
        if (mapContainerRefs.length > 0) {
          animateMaps(mapContainerRefs, { current: sectionRef.current });
        }
      }
    }, 100);

    // Clean up animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold mb-12 text-center"
        >
          Our Dispensary Location
        </h2>

        <div className="max-w-3xl mx-auto">
          {/* DC Location Card */}
          <div
            ref={(el) => (locationRefs.current[0] = el)}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            {/* Map Container with loading states */}
            <div
              ref={(el) => (mapRefs.current[0] = el)}
              className="h-64 w-full relative"
              style={{ position: "relative", overflow: "hidden" }}
            >
              {/* Loading indicator for map */}
              {!mapLoaded && !mapError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}

              {/* Error fallback for map */}
              {mapError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center p-8">
                    <svg
                      className="w-12 h-12 text-gray-400 mb-4 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <p className="font-medium text-gray-700 mb-2">
                      Map Unavailable
                    </p>
                    <p className="text-sm text-gray-600">
                      {dcLocation.address.street}, {dcLocation.address.city},{" "}
                      {dcLocation.address.state}
                    </p>
                  </div>
                </div>
              )}

              {/* Google Maps Iframe - Simple Embed */}
              <iframe
                src={dcLocation.embedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={false}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Location of ${dcLocation.name}`}
                onLoad={handleMapLoad}
                onError={handleMapError}
                className={mapLoaded ? "opacity-100" : "opacity-0"}
              ></iframe>
            </div>

            {/* Location Details */}
            <div className="p-6 relative bg-white">
              <h3 className="text-2xl font-semibold mb-2">{dcLocation.name}</h3>
              <address className="not-italic text-gray-700 mb-1">
                {dcLocation.address.street}
              </address>
              <p className="text-gray-700 mb-4">
                {dcLocation.address.city}, {dcLocation.address.state}{" "}
                {dcLocation.address.zip}
              </p>

              {/* Contact Information */}
              <div className="mb-4">
                {/* <p className="text-gray-700 mb-1">
                  <span className="font-medium">Phone:</span>{" "}
                  <a
                    href={`tel:${dcLocation.contact.phone}`}
                    className="text-cannabis-green-600 hover:text-cannabis-green-700"
                  >
                    {dcLocation.contact.phone}
                  </a>
                </p>
                <p className="text-gray-700 mb-1">
                  <span className="font-medium">Email:</span>{" "}
                  <a
                    href={`mailto:${dcLocation.contact.email}`}
                    className="text-cannabis-green-600 hover:text-cannabis-green-700"
                  >
                    {dcLocation.contact.email}
                  </a>
                </p> */}
                <p className="text-gray-700">
                  <span className="font-medium">Hours:</span> Open Daily:{" "}
                  {formattedHours}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/locations"
                  className="inline-block px-6 py-2.5 bg-cannabis-green-500 hover:bg-cannabis-green-600 text-white font-medium rounded-lg transition duration-300 text-center"
                  aria-label="View detailed location information"
                >
                  Location Details
                </Link>
                <a
                  href={dcLocation.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-2.5 border-2 border-cannabis-green-500 text-cannabis-green-600 hover:bg-cannabis-green-50 font-medium rounded-lg transition duration-300 text-center"
                  aria-label="Get directions to Street Lawyer Services"
                >
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
